import {
    Box,
    Button,
    Card,
    CardBody,
    Flex,
    Form,
    FormModalFooter,
    Icon,
    IconWithVariant
} from '@auviknetworks/iceberg-ui';
import {
    DndContext,
    DragEndEvent,
    DragOverlay,
    DragStartEvent,
    KeyboardSensor,
    PointerSensor,
    UniqueIdentifier,
    closestCenter,
    useSensor,
    useSensors
} from '@dnd-kit/core';
import {
    SortableContext,
    arrayMove,
    sortableKeyboardCoordinates,
    useSortable,
    verticalListSortingStrategy
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import Editor, { BeforeMount, Monaco } from '@monaco-editor/react';
import { Typography } from '@mui/material';
import { PageTitle } from 'application/Shared/Components/App';
import controlSchemaJSON from 'application/Shared/Components/Form/Control.schema.json';
import fieldSchemaJSON from 'application/Shared/Components/Form/Field.schema.json';
import { type FieldSchemaJson as workatoSchema } from 'application/Shared/Components/Form/Field.type';
import { FieldFactory } from 'application/Shared/Components/Form/FieldFactory';
import { type FactoryVariant } from 'application/Shared/Components/Form/types';
import workatoSchemaJSON from 'application/Shared/Components/Form/workato.schema.json';
import { KebabMenu } from 'application/Shared/Components/Menu/KebabMenu';
import { TooltipQ } from 'application/Shared/Components/Tooltips';
import { FontSizes } from 'application/Shared/Constants';
import {
    FormFactoryContextProvider,
    FormFactoryContextType
} from 'application/Shared/Contexts/FormFactoryContext';
import { Dropdown } from 'application/Shared/Dropdown';
import { useUserTheme } from 'hooks/dataAccess/User/userTheme';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { FieldForm } from './FieldForm';
import { FormContextManager } from './FormContextManager';
import conditionalForm from './conditionalForm.json';

/* Stabs */
import controls from './controls.json';
import controlsWorkato from './controlsWorkato.json';
import { flattenObject } from './flattenObject';
import groups from './groups.json';
import user from './user.mock.json';
import userFormSchema from './userForm.json';
import validation from './validation.json';

interface Edit {
    field?: workatoSchema;
    index: number;
}

// Create the array schema definition
const fieldSchemaArray = {
    $id: 'fieldSchemaArray',
    $schema: 'http://json-schema.org/draft-07/schema#',
    definitions: {
        ...controlSchemaJSON.definitions,
        'Control.schema.json': controlSchemaJSON,
        'Field.schema.json': fieldSchemaJSON,
        'workato.schema.json': workatoSchemaJSON
    },
    items: {
        $ref: 'Field.schema.json'
    },
    type: 'array'
};

const beforeMount: BeforeMount = (monaco: Monaco) => {
    monaco.languages.json.jsonDefaults.setDiagnosticsOptions({
        schemas: [
            // Import sub schemas definitions.
            {
                schema: fieldSchemaJSON,
                uri: 'Field.schema.json'
            },
            {
                schema: workatoSchemaJSON,
                uri: 'workato.schema.json'
            },
            {
                schema: controlSchemaJSON,
                uri: 'Control.schema.json'
            },
            {
                // Apply to all files
                fileMatch: ['*'],
                schema: fieldSchemaArray,
                // This URI is just for identification
                uri: 'fieldSchemaArray'
            }
        ],
        validate: true
    });
};

const SortableField = ({
    field,
    index,
    onDelete,
    onEdit,
    variant
}: {
    field: workatoSchema;
    index: number;
    onDelete: (index: number) => void;
    onEdit: (field: workatoSchema, index: number) => void;
    variant: FactoryVariant;
}) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging
    } = useSortable({ id: field.name });

    const style = {
        opacity: isDragging ? 0.5 : 1,
        transform: transform ? CSS.Transform.toString(transform) : undefined,
        transition
    };

    return (
        <Flex
            ref={setNodeRef}
            style={style}
            sx={{
                backgroundColor: isDragging ? 'gray.100' : 'transparent',
                border: isDragging ? '2px dashed gray' : 'none'
            }}
        >
            {/* Drag Handle */}
            <Button
                {...attributes}
                {...listeners}
                size="compact"
                variant="iconWithoutBorder"
                sx={{
                    '&:active': {
                        cursor: 'grabbing'
                    },
                    cursor: 'grab',
                    marginBottom: '16px'
                }}
            >
                <Icon size={16} src="DotsVertical" />
            </Button>

            {/* Field Content */}
            <FieldFactory key={field.name} schema={field} variant={variant} />

            {/* Action Buttons */}
            <Button
                data-trackid="globalAdmin_saasOps_delete"
                onClick={() => onDelete(index)}
                size="compact"
                variant="iconWithoutBorder"
                sx={{
                    marginBottom: '16px',
                    marginLeft: 'auto'
                }}
            >
                <Icon size={16} src="Trash" />
            </Button>
            <Button
                data-trackid="globalAdmin_saasOps_edit"
                onClick={() => onEdit(field, index)}
                size="compact"
                variant="iconWithoutBorder"
                sx={{
                    marginBottom: '16px'
                }}
            >
                <Icon size={16} src="Pencil" />
            </Button>
        </Flex>
    );
};

/**
 * Global Admin wrapper for the Tags component.
 *
 * @returns {JSX.Element} The GlobalTags component.
 */
export const FormBuilder = () => {
    const { t } = useTranslation();
    const [payload, setPayload] = useState<Record<string, string>>();
    const [edit, setEdit] = useState<Edit | undefined>();
    const [variant, setVariant] = useState<FactoryVariant>('form');
    const userTheme = useUserTheme();
    const [renderKey, setRenderKey] = useState<number>(0);

    const [saasOpsContext, setSaasOpsContext] =
        useState<FormFactoryContextType>({
            applicationId: '',
            clientId: ''
        });

    const handleEditorChange = (value: string | undefined) => {
        if (value === undefined) return;

        try {
            const parsedContent = JSON.parse(value);
            setFormDefinition(parsedContent as workatoSchema[]);
        } catch (error) {
            console.error('Failed to parse JSON:', error);
        }
    };

    const [formDefinition, setFormDefinition] = React.useState<workatoSchema[]>(
        []
    );

    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates
        })
    );

    const [activeId, setActiveId] = useState<UniqueIdentifier | null>(null);

    const handleDragStart = (event: DragStartEvent) => {
        const { active } = event;
        setActiveId(active.id);
    };

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;
        setActiveId(null);

        if (over && active.id !== over.id) {
            const oldIndex = formDefinition.findIndex(
                (item) => item.name === active.id
            );
            const newIndex = formDefinition.findIndex(
                (item) => item.name === over.id
            );

            setFormDefinition(arrayMove(formDefinition, oldIndex, newIndex));
        }
    };

    return (
        <>
            <PageTitle title={t('Admin.FormBuilder.title')} />
            <Flex sx={{ gap: '24px', width: '100%' }}>
                <Card sx={{ flex: 1 }}>
                    <Flex
                        sx={{
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: '16px'
                        }}
                    >
                        <Typography variant="h4">
                            {t('Admin.FormBuilder.field.definition')}
                        </Typography>
                        <Dropdown
                            labelKey="Schemas"
                            menu={[
                                {
                                    action: () =>
                                        setFormDefinition(
                                            controls as workatoSchema[]
                                        ),
                                    labelKey: 'Auvik fields',
                                    type: 'leaf'
                                },
                                {
                                    action: () =>
                                        setFormDefinition(
                                            controlsWorkato as workatoSchema[]
                                        ),
                                    labelKey: 'Workato Fields',
                                    type: 'leaf'
                                },
                                {
                                    action: () =>
                                        setFormDefinition(
                                            userFormSchema as workatoSchema[]
                                        ),
                                    labelKey: 'User example',
                                    type: 'leaf'
                                },
                                {
                                    action: () =>
                                        setFormDefinition(
                                            conditionalForm as workatoSchema[]
                                        ),
                                    labelKey: 'Conditional example',
                                    type: 'leaf'
                                },
                                {
                                    action: () =>
                                        setFormDefinition(
                                            groups as workatoSchema[]
                                        ),
                                    labelKey: 'Groups example',
                                    type: 'leaf'
                                },
                                {
                                    action: () =>
                                        setFormDefinition(
                                            validation as workatoSchema[]
                                        ),
                                    labelKey: 'Validation example',
                                    type: 'leaf'
                                }
                            ]}
                        />
                    </Flex>
                    <Editor
                        height="80vh"
                        defaultLanguage="json"
                        theme={userTheme === 'dark' ? 'vs-dark' : 'vs-light'}
                        beforeMount={beforeMount}
                        value={JSON.stringify(formDefinition, null, 2)}
                        onChange={handleEditorChange}
                    />
                </Card>
                <Flex sx={{ flex: 1 }} flexDirection="column">
                    <FormContextManager
                        clientId={saasOpsContext.clientId}
                        applicationId={saasOpsContext.applicationId}
                        setSaasOpsContext={setSaasOpsContext}
                    />

                    <Card sx={{ flex: 1 }}>
                        <Flex
                            sx={{
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                padding: '16px'
                            }}
                        >
                            <Typography variant="h4">
                                {t('Admin.FormBuilder.field.form')}
                            </Typography>

                            <Box>
                                <Button
                                    data-trackid="globalAdmin_saasOps_addField"
                                    sx={{ marginRight: '8px' }}
                                    onClick={() =>
                                        setEdit({
                                            index: formDefinition.length
                                        })
                                    }
                                    size="compact"
                                    variant="secondary"
                                >
                                    {t('Admin.FormBuilder.field.add')}
                                </Button>

                                <TooltipQ
                                    text="Rerender the form"
                                    content={
                                        <Box
                                            onClick={() =>
                                                setRenderKey((prev) => prev + 1)
                                            }
                                        >
                                            <IconWithVariant
                                                src="Reset"
                                                size={FontSizes[3]}
                                                wrapperProps={{
                                                    marginRight: '2px'
                                                }}
                                            />
                                        </Box>
                                    }
                                />
                                <KebabMenu
                                    onClick={(value: FactoryVariant) => {
                                        setVariant(value);
                                    }}
                                    options={[
                                        {
                                            key: 'form',
                                            label: 'form variant',
                                            value: 'form'
                                        },
                                        {
                                            key: 'summary',
                                            label: 'summary variant',
                                            value: 'summary'
                                        }
                                    ]}
                                />
                            </Box>
                        </Flex>

                        <FieldForm
                            isOpen={Boolean(edit)}
                            defaultValues={edit?.field}
                            handleClose={() => setEdit(undefined)}
                            handleSubmit={(values: workatoSchema) => {
                                const newFormDefinition = [...formDefinition];
                                newFormDefinition[edit!.index] = values;
                                setFormDefinition(newFormDefinition);
                            }}
                        />
                        <CardBody key={renderKey}>
                            <FormFactoryContextProvider
                                applicationId={saasOpsContext.applicationId}
                                clientId={saasOpsContext.clientId}
                            >
                                {/* eslint-disable-next-line @typescript-eslint/no-explicit-any -- we don't have a good type for user */}
                                <Form<{ user: any }>
                                    // We are passing the user object for the User Group context, and the flattened object for the form
                                    // Underscore based context.
                                    defaultValues={{
                                        user,
                                        ...flattenObject({ user })
                                    }}
                                    onSubmit={(values) => {
                                        setPayload(values);
                                    }}
                                >
                                    <DndContext
                                        sensors={sensors}
                                        collisionDetection={closestCenter}
                                        onDragStart={handleDragStart}
                                        onDragEnd={handleDragEnd}
                                    >
                                        <SortableContext
                                            items={formDefinition.map(
                                                (field) => field.name
                                            )}
                                            strategy={
                                                verticalListSortingStrategy
                                            }
                                        >
                                            {formDefinition.map(
                                                (
                                                    field: workatoSchema,
                                                    index: number
                                                ) => (
                                                    <SortableField
                                                        key={field.name}
                                                        field={field}
                                                        index={index}
                                                        variant={variant}
                                                        onDelete={(
                                                            index: number
                                                        ) => {
                                                            setFormDefinition(
                                                                (
                                                                    prevFields
                                                                ) => {
                                                                    const newFields =
                                                                        [
                                                                            ...prevFields
                                                                        ];
                                                                    newFields.splice(
                                                                        index,
                                                                        1
                                                                    );
                                                                    return newFields;
                                                                }
                                                            );
                                                        }}
                                                        onEdit={(
                                                            field: workatoSchema,
                                                            index: number
                                                        ) =>
                                                            setEdit({
                                                                field,
                                                                index
                                                            })
                                                        }
                                                    />
                                                )
                                            )}
                                        </SortableContext>

                                        <DragOverlay>
                                            {activeId ? (
                                                <Flex
                                                    sx={{
                                                        border: '2px dashed gray',
                                                        boxShadow: 'lg',
                                                        opacity: 0.8,
                                                        padding: '16px'
                                                    }}
                                                >
                                                    <FieldFactory
                                                        schema={
                                                            formDefinition.find(
                                                                (f) =>
                                                                    f.name ===
                                                                    activeId
                                                            )!
                                                        }
                                                    />
                                                </Flex>
                                            ) : null}
                                        </DragOverlay>
                                    </DndContext>
                                    <FormModalFooter>
                                        <Button
                                            data-trackid="globalAdmin_saasOps_submit"
                                            type="submit"
                                            disabled={formDefinition.length < 1}
                                        >
                                            {t('Admin.FormBuilder.submit')}
                                        </Button>
                                    </FormModalFooter>
                                </Form>
                            </FormFactoryContextProvider>
                            <Typography
                                variant="h5"
                                sx={{ margin: '16px 0 8px' }}
                            >
                                {t('Admin.FormBuilder.payload')}
                            </Typography>
                            <Editor
                                height="20vh"
                                defaultLanguage="json"
                                value={JSON.stringify(payload, null, 2)}
                                options={{ readOnly: true }}
                            />
                        </CardBody>
                    </Card>
                </Flex>
            </Flex>
        </>
    );
};
